<template>
  <button
    type="button"
    :class="componentClasses"
    @click.stop="onClick"
    @click.shift="onClickShift"
    @contextmenu.prevent="onContextMenu"
    :disabled="disabled"
  >
    <ax-loader
      v-if="showLoader"
      type="mini"
      position="absolute"
      :loader-color="loaderColor"
      :loader-size="loaderSize"
    ></ax-loader>
    <slot>
      <span v-if="showCheckbox" class="ax--btn__checkbox" :class="{ checked: checked }" />
      <span class="label" v-if="label && label.length > 0" v-html="label"></span>
      <icon-base :icon-color="iconColor" :height="iconHeight" :width="iconWidth" v-if="icon">
        <icon-arrow direction="left" v-if="icon === 'arrow-left'" />
        <icon-arrow direction="right" v-else-if="icon === 'arrow-right'" />
        <icon-checkmark v-else-if="icon === 'checkmark'" />
        <icon-circle-edit v-else-if="icon === 'circle-edit'" />
        <icon-circle-x v-else-if="icon === 'circle-x'" />
        <icon-minus v-else-if="icon === 'minus'" />
        <icon-note v-else-if="icon === 'note'" />
        <icon-plus v-else-if="icon === 'plus'" />
        <icon-refresh v-else-if="icon === 'refresh'" />
        <icon-square-x v-else-if="icon === 'square-x'" />
        <icon-select v-else-if="icon === 'select'" />
      </icon-base>
    </slot>
  </button>
</template>

<script>
import { type } from 'os'
import AxLoader from './AxLoader'
import IconArrow from '../icons/icon-arrow'
import IconBase from '../icons/icon-base'
import IconCheckmark from '../icons/icon-checkmark'
import IconCircleEdit from '../icons/icon-circle-edit'
import IconCircleX from '../icons/icon-circle-x'
import IconMinus from '../icons/icon-minus'
import IconNote from '../icons/icon-note'
import IconPlus from '../icons/icon-plus'
import IconRefresh from '../icons/icon-refresh'
import IconSquareX from '../icons/icon-square-x'
import IconSelect from '../icons/icon-select'

export default {
  name: 'ax-button',
  components: {
    AxLoader,
    IconArrow,
    IconBase,
    IconCheckmark,
    IconCircleEdit,
    IconCircleX,
    IconMinus,
    IconNote,
    IconPlus,
    IconRefresh,
    IconSquareX,
    IconSelect,
  },
  data() {
    return {
      axComponentClass: 'ax--btn',
    }
  },
  methods: {
    onClick() {
      this.$emit('click')
    },
    onClickShift() {
      this.$emit('clickShift')
    },
    onContextMenu() {
      this.$emit('context-menu')
    },
  },
  computed: {
    componentClasses() {
      let classList = [`${this.axComponentClass}`]
      if (this.buttonType) {
        classList.push(`${this.axComponentClass}--${this.buttonType}`)
      }
      // MD (medium) is the default size, so don't do anything if its this size
      if (this.size !== 'MD') {
        if (this.size === 'XL') {
          classList.push(`${this.axComponentClass}--xl`)
        }
        if (this.size === 'LG') {
          classList.push(`${this.axComponentClass}--lg`)
        }
        if (this.size === 'SM') {
          classList.push(`${this.axComponentClass}--sm`)
        }
      }
      if (this.contentVertical) {
        classList.push(`${this.axComponentClass}--vertical`)
      }
      if (this.rotate) {
        classList.push(`${this.axComponentClass}--rotate`)
      }
      if (this.icon) {
        classList.push(`${this.axComponentClass}--icon`)

        if (this.iconPosition === 'left') {
          classList.push(`flex-direction--row-reverse`)
        }
      }
      if (this.border) {
        classList.push(`ax--border`)
      }
      if (this.borderStyle) {
        classList.push(`ax--border--${this.borderStyle}`)
      }
      if (this.width && this.width === 'auto') {
        classList.push(`${this.axComponentClass}--width-auto`)
      }
      if (this.showLoader) {
        classList.push('ax--btn--loading')
      }
      if (this.iconOnly) {
        classList.push('ax--btn--icon-only')
      }

      return classList.join(' ')
    },
  },
  props: {
    label: String,
    size: {
      type: String,
      default: null,
      validator: (value) => {
        const buttonSizes = ['SM', 'MD', 'LG', 'XL']
        return buttonSizes.includes(value)
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showCheckbox: {
      type: Boolean,
      default: false,
    },
    checked: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: null,
    },
    iconPosition: {
      type: String,
      default: 'right',
      validator: (value) => {
        const iconPositions = ['left', 'right', 'center']
        return iconPositions.includes(value)
      },
    },
    iconHeight: {
      type: Number,
      default: 18,
    },
    iconWidth: {
      type: Number,
      default: 18,
    },
    iconColor: {
      type: String,
      default: '',
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    border: {
      type: Boolean,
      default: false,
    },
    borderStyle: {
      type: String,
      default: null,
      validator: (value) => {
        const borderStyles = ['rounded', 'circle']
        return borderStyles.includes(value)
      },
    },
    buttonType: {
      type: String,
      default: 'primary',
      validator: (value) => {
        const buttonTypes = ['primary', 'secondary', 'ghost', 'outline', 'link']
        return buttonTypes.includes(value)
      },
    },
    showLoader: {
      type: Boolean,
      default: false,
    },
    loaderColor: {
      type: String,
      default: 'white',
    },
    loaderSize: {
      type: String,
      default: 'md',
      validator: (value) => {
        const sizes = ['sm', 'md', 'lg']
        return sizes.includes(value)
      },
    },
    contentVertical: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: null,
    },
    rotate: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    checked: {
      immediate: true,
      handler(isChecked) {
        this.isChecked = isChecked
      },
    },
  },
}
</script>
<style lang="scss">
@import '../../../Content/scss/designtokens/animations';
@import '../../../Content/scss/designtokens/borders';
@import '../../../Content/scss/designtokens/colors';
@import '../../../Content/scss/designtokens/fontSize';
@import '../../../Content/scss/designtokens/lineHeight';
@import '../../../Content/scss/lib/breakpoint';
@import '../../../Content/scss/lib/functions';

.ax--btn {
  position: relative;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  background: transparent;
  background-clip: border-box;
  border: $border-width--thin solid transparent;
  border-radius: $border-radius--medium;
  font-size: $font-size--small;
  line-height: $line-height--button;
  text-decoration: none;
  color: $color--text-link;
  // stylelint-disable property-no-vendor-prefix
  -webkit-appearance: none;
  white-space: normal;
  user-select: none;
  transition: color $duration-immediately linear, background-color $duration-immediately linear;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &--primary {
    background: var(--cds-button-primary-color, $color-primary);
    span.label {
      color: var(--cds-button-primary-text-color, inherit);
    }
  }

  &--ghost {
    background: none;
  }

  &--vertical {
    flex-direction: column;
  }
  //cycle-16 - Edit cart feature
  &--link {
    background-color: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    color: var(--v-secondary-lighten1, #595959) !important;
    text-transform: uppercase !important;
    font-weight: 500 !important;
    font-size: 0.7rem !important;
    padding: 2px 5px !important;
    margin: 0 !important;
  }

  &--loading {
    pointer-events: none;

    .label {
      opacity: 0.35;
    }
  }

  &.flex-direction--row-reverse {
    flex-direction: row-reverse;
    .label {
      padding-left: 0.25em;
    }
    svg {
      margin-right: 0;
    }
  }

  &--icon {
    background-color: map-get($colors, white);
    height: pxrem(28);
    line-height: pxrem(28);
    padding: 0;
    text-align: center;
    width: pxrem(28);

    svg {
      display: block;
      height: 100%;
      margin: 0 auto;
      overflow: visible;
      width: 18px;

      margin-left: pxrem(8);

      g {
        fill: $color-primary;
      }
    }
  }

  &--icon-only {
    justify-content: space-around;

    svg {
      margin: 0;
    }
  }

  &--width-auto {
    width: auto;
  }

  &--xl {
    font-size: pxrem(18);
    padding: pxrem(20) pxrem(25);
  }

  &--lg {
    font-size: pxrem(16);
    padding: pxrem(10) pxrem(20);
  }

  &__divider {
    display: inline-block;
    margin: 0 0.75em;
  }

  &.ax--border--circle {
    border-radius: 100%;
    border: 1px solid #007aff;
  }

  .ax--btn__checkbox {
    position: relative;
    background-color: #fff;
    width: 28px;
    height: 28px;
    border-radius: 0px;
    border: none;
    padding: 0;
    margin: 0 18px 0 0;
  }

  .ax--btn__checkbox.checked {
    &:after {
      position: absolute;
      content: '';
      left: 9px;
      top: 5px;
      width: 7px;
      height: 12px;
      border: solid black;
      border-width: 0 3px 3px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }
}
.ax--btn[disabled] {
  opacity: 0.5;
  cursor: default;
}
.ax--btn--primary {
  &:hover {
    background-color: var(--cds-button-primary-color-hover, $color-primary);
  }
}
</style>
